import { toast } from 'sonner';
import {
  CHECK_URL_EMBEDDABLE,
  type CheckUrlEmbeddableResult,
  type CheckUrlEmbeddableVariables,
} from '@scio3/graphql-client';
import { registerActionHandler } from '../action-registry';

/**
 * `verifyEmbeddableUrl` — verifies that a user-entered URL can be embedded inside
 * the social-player iframe. Mirrors scio1's `checkHeaderUrl` (advancedWebViewModal).
 *
 * Usage modes:
 *  - Manual button click  (`isSaveCheck: false` / undefined) — toasts both success
 *    and failure for user feedback.
 *  - Pre-save run         (`isSaveCheck: true`, via `action.runOnSave`)         —
 *    suppresses the success toast; returns `{ ok: false, reason }` to block save
 *    when the URL is not embeddable. The pre-save pipeline surfaces `reason` as
 *    an error toast.
 *
 * Manifest usage:
 *   { key: 'webPageUrl', type: 'string', action: {
 *       label: 'Verify URL',
 *       handler: 'verifyEmbeddableUrl',
 *       requires: ['webPageUrl'],
 *       runOnSave: true,
 *       displayOptions: { showIf: { displayIniFrame: [true] } }
 *   } }
 */
registerActionHandler('verifyEmbeddableUrl', async ({ values, apolloClient, t, isSaveCheck }) => {
  const url = ((values.webPageUrl as string) ?? '').trim();
  if (!url) {
    // No URL: button click would already be disabled by `requires`; on save-check
    // we treat empty as a non-blocker (let the required-field validator handle it).
    if (!isSaveCheck) toast.error(t('Please enter a URL first'));
    return;
  }

  const { data } = await apolloClient.query<CheckUrlEmbeddableResult, CheckUrlEmbeddableVariables>({
    query: CHECK_URL_EMBEDDABLE,
    variables: { url },
    fetchPolicy: 'no-cache',
  });

  const result = data?.checkUrlEmbeddable;
  const canEmbed = !!result?.canEmbed;
  const reason = result?.reason ?? t('URL cannot be embedded in an iframe');

  if (canEmbed) {
    // Manual click: confirm success. Save check: stay quiet, let the save proceed.
    if (!isSaveCheck) toast.success(t('URL can be embedded'));
    return { ok: true };
  }

  // Not embeddable. Manual click toasts the error inline; save-check leaves the
  // toast to the pre-save pipeline. Both paths return the same `{ ok: false }` so
  // the cache (and the trailing-button alert icon) reflects the failure either way.
  if (!isSaveCheck) toast.error(reason);
  return { ok: false, reason };
});
